/**
 * ListKanbanPanel — zakładka „Kanban" w Kreatorze Listy.
 *
 * Odwzorowanie zakładki „Kanban" z SellCalla: pole grupujące + pola na kartce
 * + styl tablicy. Zapis przez `useTableLayout(slug)` do `list_config.kanbanConfig`
 * (PUT — zachowuje kolumny/sort/filtry/quickFilters/pageConfig).
 *
 * Pola do wyboru = kolumny aktywnego layoutu (etykiety z `list-column-registry`,
 * jak zakładki Kolumny / Szybkie filtry). Faktyczne opcje grupowania (wartości
 * kolumn tablicy) rozwiązuje runtime tablicy z definicji kolumn strony — tu
 * trzymamy tylko id pola grupującego, listę pól kafelka i styl.
 *
 * „Edytuj wygląd karty" (wizualny designer kafelka → `kanbanConfig.cardLayout`)
 * dochodzi w osobnym kroku — na razie przycisk wyszarzony „wkrótce".
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  defaultKanbanBoardStyle,
  type KanbanBoardStyle,
  type KanbanColumnRadius,
  type KanbanHeaderStyle,
} from '@saas/shared';
import { Save } from 'lucide-react';

import { Dropdown } from '@/components/Dropdown';
import { useToast } from '@/contexts/ToastContext';
import type { LayoutElement } from '@/lib/layout-tree';

import { KanbanCardDesigner } from './KanbanCardDesigner';
import { useColumnLabel } from './list-column-registry';
import { useTableLayout } from './useTableLayout';

const RADII: KanbanColumnRadius[] = ['none', 'small', 'medium', 'large'];
const FONT_WEIGHTS: KanbanHeaderStyle['fontWeight'][] = ['normal', 'medium', 'semibold', 'bold'];
const ALIGNS: KanbanHeaderStyle['align'][] = ['left', 'center', 'right'];
const COLOR_INDICATORS: KanbanHeaderStyle['colorIndicator'][] = ['dot', 'bar', 'none'];
const COUNTER_STYLES: KanbanHeaderStyle['counterStyle'][] = ['badge', 'plain'];

function mergeStyle(partial: Partial<KanbanBoardStyle> | undefined): KanbanBoardStyle {
  return {
    ...defaultKanbanBoardStyle,
    ...(partial ?? {}),
    header: { ...defaultKanbanBoardStyle.header, ...(partial?.header ?? {}) },
  };
}

export function ListKanbanPanel({ slug }: { slug: string }): React.ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const { layout, isLoading, saveLayout, isSaving } = useTableLayout(slug);
  // Hub Krok 2: provider etykiet świadomy trybu `template` (pola szablonu po nazwie).
  const columnLabel = useColumnLabel(slug);

  const [groupBy, setGroupBy] = useState('');
  const [cardFields, setCardFields] = useState<string[]>([]);
  const [style, setStyle] = useState<KanbanBoardStyle>(defaultKanbanBoardStyle);
  const [cardLayout, setCardLayout] = useState<LayoutElement[] | null>(null);
  const [designerOpen, setDesignerOpen] = useState(false);

  useEffect(() => {
    const kc = layout?.layoutConfig?.kanbanConfig;
    if (kc) {
      setGroupBy(kc.groupByFieldId ?? '');
      setCardFields(kc.cardFields ?? []);
      setStyle(mergeStyle(kc.boardStyle));
      setCardLayout((kc.cardLayout as LayoutElement[] | null) ?? null);
    }
  }, [layout]);

  if (isLoading) return <p className="text-sm text-text-tertiary">…</p>;
  if (!layout) {
    return <p className="text-sm text-text-muted">{t('tenant.listBuilder.columns.noLayout')}</p>;
  }

  const columns = layout.layoutConfig.columns ?? [];
  const label = (id: string): string => columnLabel(id) ?? id;

  const hasColumns = columns.length > 0;

  const toggleCardField = (id: string) =>
    setCardFields((prev) => (prev.includes(id) ? prev.filter((f) => f !== id) : [...prev, id]));

  const setHeader = <K extends keyof KanbanHeaderStyle>(key: K, value: KanbanHeaderStyle[K]) =>
    setStyle((s) => ({ ...s, header: { ...s.header, [key]: value } }));

  const setStyleField = <K extends keyof KanbanBoardStyle>(key: K, value: KanbanBoardStyle[K]) =>
    setStyle((s) => ({ ...s, [key]: value }));

  const handleSave = async () => {
    if (!groupBy) {
      toast.show({ variant: 'error', title: t('tenant.listBuilder.kanban.missingGroupBy') });
      return;
    }
    await saveLayout({
      ...layout.layoutConfig,
      kanbanConfig: {
        groupByFieldId: groupBy,
        cardFields,
        cardLayout: cardLayout as unknown as Record<string, unknown>[] | null,
        boardStyle: style,
      },
    });
    toast.show({ variant: 'success', title: t('tenant.listBuilder.kanban.saved') });
  };

  // Pola dostępne w designerze kafelka = kolumny (z etykietami), bez grupującej.
  const designerFields = columns
    .filter((c) => c.id !== groupBy)
    .map((c) => ({ id: c.id, label: label(c.id) }));

  return (
    <div className="space-y-6" data-testid="list-kanban-panel">
      <p className="text-sm text-text-tertiary">{t('tenant.listBuilder.kanban.intro')}</p>

      {!hasColumns && (
        <p className="rounded-lg border border-border-secondary bg-bg-secondary px-4 py-3 text-sm text-text-muted">
          {t('tenant.listBuilder.kanban.noColumns')}
        </p>
      )}

      {/* Pole grupujące */}
      <div className="space-y-1.5">
        <label htmlFor="kanban-groupby" className="block text-sm font-medium text-text-primary">
          {t('tenant.listBuilder.kanban.groupByField')}
        </label>
        <Dropdown
          id="kanban-groupby"
          options={[
            { value: '', label: t('tenant.listBuilder.kanban.selectField') },
            ...columns.map((c) => ({ value: c.id, label: label(c.id) })),
          ]}
          value={groupBy}
          onChange={(val) => setGroupBy(val)}
          className="max-w-sm px-2 py-1.5"
          data-testid="kanban-groupby-select"
        />
        <p className="text-xs text-text-muted">{t('tenant.listBuilder.kanban.groupByFieldHint')}</p>
      </div>

      {/* Pola na kartce */}
      {groupBy && (
        <fieldset className="space-y-2">
          <legend className="text-sm font-medium text-text-primary">
            {t('tenant.listBuilder.kanban.cardFields')}
          </legend>
          <p className="text-xs text-text-muted">{t('tenant.listBuilder.kanban.cardFieldsHint')}</p>
          <div className="space-y-1.5 rounded-lg border border-border-secondary bg-bg-secondary p-3">
            {columns
              .filter((c) => c.id !== groupBy)
              .map((c) => (
                <label key={c.id} className="flex cursor-pointer items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={cardFields.includes(c.id)}
                    onChange={() => toggleCardField(c.id)}
                    className="h-4 w-4 rounded border-border-secondary accent-accent-600"
                    data-testid={`kanban-card-field-${c.id}`}
                  />
                  <span className="text-text-primary">{label(c.id)}</span>
                </label>
              ))}
          </div>
        </fieldset>
      )}

      {/* Edytuj wygląd karty — wizualny designer kafelka */}
      {groupBy && (
        <div className="space-y-1.5">
          <button
            type="button"
            onClick={() => setDesignerOpen(true)}
            className="inline-flex items-center gap-2 rounded-lg border border-border-secondary px-3 py-1.5 text-sm text-text-primary hover:border-accent-400"
            data-testid="kanban-edit-card-layout"
          >
            {t('tenant.listBuilder.kanban.editCardLayout')}
            {cardLayout && cardLayout.length > 0 && (
              <span className="rounded bg-accent-50 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-accent-700">
                {t('tenant.listBuilder.kanban.designer.configured')}
              </span>
            )}
          </button>
          <p className="text-xs text-text-muted">
            {t('tenant.listBuilder.kanban.editCardLayoutHint')}
          </p>
          {cardLayout && cardLayout.length > 0 && (
            <button
              type="button"
              onClick={() => setCardLayout(null)}
              className="text-xs text-accent-600 hover:text-accent-700"
              data-testid="kanban-clear-card-layout"
            >
              {t('tenant.listBuilder.kanban.designer.clear')}
            </button>
          )}
        </div>
      )}

      <KanbanCardDesigner
        open={designerOpen}
        onClose={() => setDesignerOpen(false)}
        fields={designerFields}
        initialLayout={cardLayout}
        onSave={(next) => setCardLayout(next.length > 0 ? next : null)}
      />

      {/* Styl tablicy */}
      <fieldset className="space-y-4 rounded-lg border border-border-secondary p-4">
        <legend className="px-1 text-sm font-medium text-text-primary">
          {t('tenant.listBuilder.kanban.boardStyle')}
        </legend>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <NumberField
            label={t('tenant.listBuilder.kanban.columnWidth')}
            value={style.columnWidth}
            min={180}
            max={640}
            onChange={(n) => setStyleField('columnWidth', n)}
            testId="kanban-style-column-width"
          />

          <div className="space-y-1">
            <label
              htmlFor="kanban-style-radius"
              className="block text-xs font-medium text-text-secondary"
            >
              {t('tenant.listBuilder.kanban.columnRadius')}
            </label>
            <Dropdown
              id="kanban-style-radius"
              options={RADII.map((r) => ({
                value: r,
                label: t(`tenant.listBuilder.kanban.radius.${r}`),
              }))}
              value={style.columnRadius}
              onChange={(val) => setStyleField('columnRadius', val as KanbanColumnRadius)}
              className="px-2 py-1"
              data-testid="kanban-style-radius"
            />
          </div>

          <NumberField
            label={t('tenant.listBuilder.kanban.cardGap')}
            value={style.cardGap}
            min={0}
            max={48}
            onChange={(n) => setStyleField('cardGap', n)}
            testId="kanban-style-card-gap"
          />

          <NumberField
            label={t('tenant.listBuilder.kanban.cardPadding')}
            value={style.cardPadding}
            min={0}
            max={48}
            onChange={(n) => setStyleField('cardPadding', n)}
            testId="kanban-style-card-padding"
          />

          <div className="space-y-1">
            <label
              htmlFor="kanban-style-bg"
              className="block text-xs font-medium text-text-secondary"
            >
              {t('tenant.listBuilder.kanban.columnBgColor')}
            </label>
            <div className="flex items-center gap-2">
              <input
                id="kanban-style-bg"
                type="color"
                value={style.columnBgColor ?? '#f3f4f6'}
                onChange={(e) => setStyleField('columnBgColor', e.target.value)}
                className="h-8 w-12 cursor-pointer rounded border border-border-secondary bg-bg-elevated"
                data-testid="kanban-style-bg-color"
              />
              {style.columnBgColor && (
                <button
                  type="button"
                  onClick={() => setStyleField('columnBgColor', null)}
                  className="text-xs text-accent-600 hover:text-accent-700"
                >
                  {t('tenant.listBuilder.kanban.resetBgColor')}
                </button>
              )}
            </div>
          </div>

          <label className="flex items-center gap-2 self-end text-sm text-text-primary">
            <input
              type="checkbox"
              checked={style.showColumnBorder}
              onChange={(e) => setStyleField('showColumnBorder', e.target.checked)}
              className="h-4 w-4 rounded border-border-secondary accent-accent-600"
              data-testid="kanban-style-show-border"
            />
            {t('tenant.listBuilder.kanban.showColumnBorder')}
          </label>
        </div>

        {/* Styl nagłówka kolumny */}
        <div className="space-y-3 border-t border-border-secondary pt-3">
          <p className="text-xs font-medium uppercase tracking-wide text-text-muted">
            {t('tenant.listBuilder.kanban.headerStyle')}
          </p>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <NumberField
              label={t('tenant.listBuilder.kanban.headerFontSize')}
              value={style.header.fontSize}
              min={8}
              max={48}
              onChange={(n) => setHeader('fontSize', n)}
              testId="kanban-style-header-font-size"
            />

            <div className="space-y-1">
              <label
                htmlFor="kanban-style-header-weight"
                className="block text-xs font-medium text-text-secondary"
              >
                {t('tenant.listBuilder.kanban.headerFontWeight')}
              </label>
              <Dropdown
                id="kanban-style-header-weight"
                options={FONT_WEIGHTS.map((w) => ({
                  value: w,
                  label: t(`tenant.listBuilder.kanban.fontWeight.${w}`),
                }))}
                value={style.header.fontWeight}
                onChange={(val) => setHeader('fontWeight', val as KanbanHeaderStyle['fontWeight'])}
                className="px-2 py-1"
                data-testid="kanban-style-header-weight"
              />
            </div>

            <NumberField
              label={t('tenant.listBuilder.kanban.headerPadding')}
              value={style.header.padding}
              min={0}
              max={64}
              onChange={(n) => setHeader('padding', n)}
              testId="kanban-style-header-padding"
            />

            <div className="space-y-1">
              <label
                htmlFor="kanban-style-header-align"
                className="block text-xs font-medium text-text-secondary"
              >
                {t('tenant.listBuilder.kanban.headerAlign')}
              </label>
              <Dropdown
                id="kanban-style-header-align"
                options={ALIGNS.map((a) => ({
                  value: a,
                  label: t(`tenant.listBuilder.kanban.align.${a}`),
                }))}
                value={style.header.align}
                onChange={(val) => setHeader('align', val as KanbanHeaderStyle['align'])}
                className="px-2 py-1"
                data-testid="kanban-style-header-align"
              />
            </div>

            <div className="space-y-1">
              <label
                htmlFor="kanban-style-header-indicator"
                className="block text-xs font-medium text-text-secondary"
              >
                {t('tenant.listBuilder.kanban.colorIndicator')}
              </label>
              <Dropdown
                id="kanban-style-header-indicator"
                options={COLOR_INDICATORS.map((c) => ({
                  value: c,
                  label: t(`tenant.listBuilder.kanban.indicator.${c}`),
                }))}
                value={style.header.colorIndicator}
                onChange={(val) =>
                  setHeader('colorIndicator', val as KanbanHeaderStyle['colorIndicator'])
                }
                className="px-2 py-1"
                data-testid="kanban-style-header-indicator"
              />
            </div>

            <div className="space-y-1">
              <label
                htmlFor="kanban-style-header-counter"
                className="block text-xs font-medium text-text-secondary"
              >
                {t('tenant.listBuilder.kanban.counterStyle')}
              </label>
              <Dropdown
                id="kanban-style-header-counter"
                options={COUNTER_STYLES.map((c) => ({
                  value: c,
                  label: t(`tenant.listBuilder.kanban.counter.${c}`),
                }))}
                value={style.header.counterStyle}
                onChange={(val) =>
                  setHeader('counterStyle', val as KanbanHeaderStyle['counterStyle'])
                }
                className="px-2 py-1"
                data-testid="kanban-style-header-counter"
              />
            </div>
          </div>
        </div>
      </fieldset>

      <div className="flex justify-end">
        <button
          type="button"
          onClick={handleSave}
          disabled={isSaving || !groupBy}
          className="inline-flex items-center gap-2 rounded-lg bg-accent-600 px-4 py-1.5 text-sm text-white hover:bg-accent-700 disabled:opacity-50"
          data-testid="kanban-save"
        >
          <Save size={16} aria-hidden="true" />
          {isSaving
            ? t('tenant.listBuilder.kanban.saving')
            : t('tenant.listBuilder.kanban.saveBtn')}
        </button>
      </div>
    </div>
  );
}

function NumberField({
  label,
  value,
  min,
  max,
  onChange,
  testId,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  onChange: (n: number) => void;
  testId: string;
}): React.ReactElement {
  return (
    <div className="space-y-1">
      <label htmlFor={testId} className="block text-xs font-medium text-text-secondary">
        {label}
      </label>
      <input
        id={testId}
        type="number"
        min={min}
        max={max}
        value={value}
        onChange={(e) => {
          const n = parseInt(e.target.value, 10);
          if (!Number.isNaN(n)) onChange(Math.min(max, Math.max(min, n)));
        }}
        className="w-28 rounded border border-border-secondary bg-bg-elevated px-2 py-1 text-sm text-text-primary"
        data-testid={testId}
      />
    </div>
  );
}
